import { mapUserSettingsToTimeRangeFilter } from "@/common/utilities/visibilityUserSettingsHelper";
import { StatusTag, Tooltip } from "@chr/eds-react";
import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "@/react-app-env";

type TimeFrameFilterLabelProps = {
    widgetId: number;
}

type TimeFrameFilter = {
    dateField: string;
    timeRange: string;
}

const DateFields = {
    CreatedDate: 'createdDate',
    ActualPickupDate: 'actualPickupDate',
    ActualDeliveryDate: 'actualDeliveryDate',
    EarliestPickupDate: 'earliestPickupDate',
    EarliestDeliveryDate: 'earliestDeliveryDate',
    ScheduledPickupDate: 'scheduledPickupDate',
    ScheduledDeliveryDate: 'scheduledDeliveryDate',
} as const;

const TimeRanges = {
    Last30Days: 'last30Days',
    Last60Days: 'last60Days',
    Last90Days: 'last90Days',
    Last180Days: 'last180Days',
    AllAvailable: 'allAvailable',
} as const

const DefaultTimeFrameFilter: TimeFrameFilter = {
    dateField: DateFields.EarliestDeliveryDate,
    timeRange: TimeRanges.Last60Days,
}

const TimeFrameFilterLabel = ({ widgetId }: TimeFrameFilterLabelProps) =>  {     
    const [timeRange, setTimeRange] = useState<TimeFrameFilter>(DefaultTimeFrameFilter);
    // Get userSettings from Redux
    const userSettings = useSelector((state: AppState) => state.userSettingsState.userSettings);
    const isLoading = useSelector((state: AppState) => state.userSettingsState.isLoading);

    const getDateFieldLabel = (key: string): string => {
        switch (key) {
            case DateFields.CreatedDate: return 'creation day';
            case DateFields.ActualPickupDate: return 'actual pickup day';
            case DateFields.ActualDeliveryDate: return 'actual delivery day';
            case DateFields.EarliestPickupDate: return 'requested pickup day';
            case DateFields.EarliestDeliveryDate: return 'requested delivery day';
            case DateFields.ScheduledPickupDate: return 'scheduled pickup day';
            case DateFields.ScheduledDeliveryDate: return 'scheduled delivery day';
            default: return 'unknown date field';
        }
    };

    const getTimeRangeLabel = (key: string): string => {
        switch (key) {
            case TimeRanges.Last30Days: return 'Last 30 days';
            case TimeRanges.Last60Days: return 'Last 60 days';
            case TimeRanges.Last90Days: return 'Last 90 days';
            case TimeRanges.Last180Days: return 'Last 180 days';
            case TimeRanges.AllAvailable: return 'All available';
            default: return 'Unknown time range';
        }
    };

    const getTooltip = () => {
        return `${getTimeRangeLabel(timeRange.timeRange)} based on ${getDateFieldLabel(timeRange.dateField)}`;
    }

    useEffect(() => {
      if (!isLoading && userSettings) {
        // Map API response to TimeFrameFilter format
        const filter = mapUserSettingsToTimeRangeFilter(userSettings);
        setTimeRange({
          dateField: filter.dateField || DefaultTimeFrameFilter.dateField,
          timeRange: filter.timeRange || DefaultTimeFrameFilter.timeRange,
        });
      }
    }, [userSettings, isLoading]);

    if (isLoading) {
      return null;
    }

    const getTimeFrameFilter = (widgetId: number) => {
        if (!timeRange) {
            return <></>;
        }

        return (
            <Tooltip title={getTooltip()} data-testid={`timeframe-tooltip-${widgetId}`}>
                <StatusTag
                    label={timeRange.timeRange ? getTimeRangeLabel(timeRange.timeRange) : ''}
                    severity="neutral"
                    size="compact"
                    labelSize="small"
                    icon={false}            
                    data-testid={`timeframe-label-${widgetId}`}
                />
            </Tooltip>
        );
    };

    return (
        <Box className="widget-status">
            {getTimeFrameFilter(widgetId)}
        </Box>
    );
}

export default TimeFrameFilterLabel;